import styles from "@/styles/Users.module.scss";
import { useTranslation, Trans } from "react-i18next";
import { getBuildingId, WELCOME_USER_ADMINISTRATION_URL } from "@/lib";
import i18n from "@/i18n/config";
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { ColumnFiltersState, RowSelectionState } from "@tanstack/react-table";
import type { Table as ReactTable } from "@tanstack/react-table";
import { NotificationType, Timestamp, User, UserWithMetadata } from "@/types";
import {
  Button,
  DropdownMenu,
  Icon,
  InputField,
  LoadingSpinner,
  PopupCard,
  Toggle,
} from "@app-components";
import {
  useEffect,
  useState,
  lazy,
  Suspense,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useUploadUsers } from "@/hooks/uploadUsers";
import { useRouterStore, useSnackbarStore } from "@/store/zustandStore";
import { Data } from "react-spreadsheet-import/types/types";
import AddUserModal from "@/components/company_admin/company_users/AddUserModal";
import {
  companyOptions,
  companySubdivisionsOptions,
  companyUsersOptions,
  createUserInAccessControlSystem,
  deleteUser,
  getExportedCompanyUsers,
  getImageFunction,
  updateUser,
  useBuilding,
  useCompanyContact,
  useCompanySubdivisions,
  userMetadataOptions,
} from "@/api";
import { useQueryClient } from "@tanstack/react-query";
import UsersTable from "@/components/company_admin/company_users/UsersTable";
import { AnimatePresence, motion } from "framer-motion";
import { useCompanyAdminAccess } from "@/hooks/useCompanyAdminAccess";
import {
  firebaseTimestampToDate,
  getSelectedItemsInDisplayOrder,
  processInBatches,
} from "@/lib";
import { PrintAccessCard } from "@/components/badge_printing/PrintAccessCard";
import {
  addMonths,
  endOfQuarter,
  format,
  isAfter,
  isBefore,
  startOfDay,
  startOfQuarter,
} from "date-fns";
import { useIsBuildingAdmin } from "@/hooks/useBuildingAdminAccess";
import _ from "lodash";
import { usePrompt } from "@/hooks/usePrompt";
import { useCheckDefaultRolesBeforeIntegration } from "@/hooks/useCheckDefaultRolesBeforeIntegration";
import { BulkCardOrdersModal } from "@/components/access_cards/BulkCardOrdersModal";
import BulkAssignSubdivision from "@/components/company_admin/company_users/BulkAssignSubdivision";
import { useWelcomeFoodAccess } from "@/hooks/useWelcomeFoodAccess";
import FoodDealModal from "@/components/food/FoodDealModal";
import { useBuildingQuery } from "@/components/RouterSetup";
import { AcsConnectivityBanner } from "@/components/AcsConnectivityBanner";
import { useAccessAdminPermission } from "@/hooks/useAccessAdminPermission";

type UsersTableRow = UserWithMetadata & selectType & { food: boolean };
type selectType = {
  selectCol?: string;
};

const SpreadsheetImporter = lazy(
  () => import("@/components/company_admin/company_users/SpreadsheetImporter"),
);

const searchableColumns: Array<keyof User> = [
  "firstName",
  "lastName",
  "email",
  "phone",
];

export const Route = createFileRoute("/_portal/company/$companyId/users/")({
  component: Users,
  validateSearch: (search: { inactive?: boolean }) => search,
  loader: async ({ params }) => {
    const { queryClient } = useRouterStore.getState();
    const { companyId } = params;
    if (!queryClient) return;
    queryClient.ensureQueryData(companyOptions(companyId));
    queryClient.ensureQueryData(companySubdivisionsOptions(companyId));
    queryClient.fetchQuery(companyUsersOptions(companyId));
  },
});

function Users() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { inactive: showInactiveUsersOnly } = useSearch({ from: Route.id });
  const { isBuildingAdmin } = useIsBuildingAdmin();
  const { companyId } = Route.useParams();
  const { hasWelcomeFood } = useWelcomeFoodAccess(true);
  const { isCompanyAccessAdmin } = useAccessAdminPermission();
  const { allowCompanyAdminAccess } = useCompanyAdminAccess(
    companyId,
    companyId,
  );
  const canManageCompanyAccess = isCompanyAccessAdmin(companyId);
  const {
    data: users,
    isLoading,
    error: companyUsersError,
  } = useBuildingQuery(companyUsersOptions(companyId));
  const { isLoading: isLoadingMetaData, data: userMetadata } = useBuildingQuery(
    {
      ...userMetadataOptions(
        users?.map((user: User) => user.userId) || [],
        companyId,
      ),
      enabled: !!users && !!companyId,
    },
  );

  const { data: company, error: companyError } = useBuildingQuery(
    companyOptions(companyId),
  );
  const { data: subdivisions } = useCompanySubdivisions(companyId);
  const { data: building } = useBuilding();
  const { data: companyContact } = useCompanyContact(company?.buildingTenantId);
  const queryClient = useQueryClient();
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [exportingUsers, setExportingUsers] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [showFoodDealModal, setShowFoodDealModal] = useState(false);
  const [printCardsOpen, setPrintCardsOpen] = useState(false);
  const [assignSubdivisionOpen, setAssignSubdivisionOpen] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [searchTerm, setSearchTerm] = useState("");
  const { submitUsers, isSubmitting } = useUploadUsers(() =>
    setShowSendEmailModal(false),
  );
  const { addNotification } = useSnackbarStore();
  const [isDoingUserAction, setIsDoingUserAction] = useState(false);
  const [importedData, setImportedData] = useState<Data<string>[]>([]);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const csvTemplateFileUrl = "/template.csv";
  const xlsxTemplateFileUrl = "/template.xlsx";
  const selectedUsersCount = Object.keys(rowSelection).length;

  const tableInstanceRef = useRef<ReactTable<UsersTableRow> | null>(null);
  const [isTableReady, setIsTableReady] = useState(false);
  // Filters that were active before entering the inactive-users-only view, so
  // they can be re-applied when the user leaves that view again.
  const savedFiltersRef = useRef<ColumnFiltersState | null>(null);
  const didSaveFiltersRef = useRef(false);
  const { checkAndProceed, isWaiting: isRolesCheckWaiting } =
    useCheckDefaultRolesBeforeIntegration(
      company?.buildingTenantId ?? "",
      !!building?.accessControlSystemIntegration,
      !!company?.accessControlSystemIntegration,
    );

  const isAcsDown =
    (building?.acsConnectivityGuardEnabled && building?.acsConnectivityLost) ??
    false;

  usePrompt({
    promptOnNavigate: false,
    isOpen: showDeletionModal,
    title: t("EDIT_USER.DELETE_MODAL.HEADER"),
    subtitle: (
      <Trans
        i18nKey="USERS.CONFIRM_DELETION"
        values={{
          selectedUsersCount,
        }}
        components={{
          bold: (
            <strong style={{ color: "var(--wlcm-color-danger-red)" }}></strong>
          ),
          br: <br />,
        }}
      />
    ),
    confirmText: t("USERS.DELETE_USERS"),
    confirmButtonProps: { danger: true },
    onConfirm: () => {
      setShowDeletionModal(false);
      deleteMultipleUsers();
    },
    onCancel: () => setShowDeletionModal(false),
  });

  const renderFoodButton = hasWelcomeFood && !!company?.foodDeals?.length;
  const renderPrintButton =
    building?.allowBadgePrinting &&
    (isBuildingAdmin || company?.acsAllowCompanyAdminsCardPrinting);
  const renderIntegrateUserButton =
    building?.accessControlSystemIntegration &&
    company?.accessControlSystemIntegration;
  const renderOrderCardButton = !building?.disablePlasticCardOrdering;

  if (companyUsersError || companyError) {
    throw new Error("Error fetching company users", {
      cause: companyUsersError || companyError,
    });
  }

  const handleTableReady = useCallback((table: ReactTable<UsersTableRow>) => {
    tableInstanceRef.current = table;
    setIsTableReady(true);
  }, []);

  // Show deletion warning banner if at least one user has not been active this quarter and we are within 2 weeks of the end of the quarter
  const showDeletionWarningBanner = useMemo(() => {
    if (!users?.length || !building?.runQuarterlyUserPurge) return false;
    const now = new Date();
    return users.some((user) => {
      const lastActiveDate = user.lastActiveDate
        ? firebaseTimestampToDate(user.lastActiveDate)
        : null;
      return (
        // notification is always sent out on the 16th of the quarter end month
        lastActiveDate &&
        isBefore(lastActiveDate, startOfQuarter(now)) &&
        isAfter(now, startOfDay(endOfQuarter(now).setDate(16)))
      );
    });
  }, [users, building]);

  useEffect(() => {
    let usersWithMetadata: User[] = users || [];
    if (userMetadata) {
      usersWithMetadata =
        users?.map((user: User) => {
          const metadata = _.castArray(userMetadata).find(
            (meta) => meta.userId === user.userId,
          );
          if (!metadata) return user;
          return {
            ...user,
            createdAt: metadata.createdAt
              ? new Date(metadata.createdAt)
              : undefined,
            lastLogin: metadata.lastLogin
              ? new Date(metadata.lastLogin)
              : undefined,
            disabled: metadata.disabled,
          };
        }) || [];
    }

    if (showInactiveUsersOnly) {
      usersWithMetadata = usersWithMetadata.filter((user) => {
        const lastActiveDate = user.lastActiveDate
          ? firebaseTimestampToDate(user.lastActiveDate)
          : null;
        return (
          lastActiveDate && isBefore(lastActiveDate, startOfQuarter(new Date()))
        );
      });
    }

    if (searchTerm && usersWithMetadata) {
      // get every user that has an attribute from searchableColumns that starts with the searchTerm
      const searchTerms = searchTerm.toLowerCase().split(" ");
      setFilteredUsers(
        usersWithMetadata.filter((user: User) =>
          searchTerms.every((term) =>
            searchableColumns.some((column) =>
              (user[column]?.toString().toLowerCase() || "").startsWith(term),
            ),
          ),
        ),
      );
    } else {
      setFilteredUsers(usersWithMetadata || []);
    }
  }, [searchTerm, users, userMetadata, showInactiveUsersOnly]);

  useEffect(() => {
    setRowSelection({});
  }, [showInactiveUsersOnly]);

  // When only inactive users are shown, start with a clean slate: remember the
  // currently applied table filters and clear them. When the inactive view is
  // left again, the previously applied filters are restored.
  useEffect(() => {
    const table = tableInstanceRef.current;
    if (!table) return;

    if (showInactiveUsersOnly) {
      if (!didSaveFiltersRef.current) {
        savedFiltersRef.current = table.getState().columnFilters;
        didSaveFiltersRef.current = true;
      }
      table.setColumnFilters([]);
    } else if (didSaveFiltersRef.current) {
      table.setColumnFilters(savedFiltersRef.current ?? []);
      savedFiltersRef.current = null;
      didSaveFiltersRef.current = false;
    }
  }, [showInactiveUsersOnly, isTableReady]);

  const deleteMultipleUsers = async () => {
    setIsDoingUserAction(true);

    try {
      const selectedUsers = getSelectedItemsInDisplayOrder(
        tableInstanceRef.current,
      );

      const results = await processInBatches(
        selectedUsers,
        20,
        async (user) => {
          return deleteUser(
            user.userId,
            user.email,
            companyId,
            user.firstName,
            user.lastName,
          );
        },
      );

      const failedDeletions = results.filter((r) => r.status === "rejected");
      const successfulDeletions = results.filter(
        (r) => r.status === "fulfilled",
      );

      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "users"],
      });
      setRowSelection({});

      if (successfulDeletions.length > 0) {
        addNotification(
          t("NOTIFICATIONS.DOCUMENT_DELETED", {
            entity: `${successfulDeletions.length} ${t("PAGES.USERS.HEADER").toLowerCase()}`,
          }),
          NotificationType.SUCCESS,
        );
      }

      if (failedDeletions.length > 0) {
        addNotification(
          t("NOTIFICATIONS.DELETE_PARTIAL_FAILURE", {
            entity: `${failedDeletions.length} ${t("PAGES.USERS.HEADER").toLowerCase()}`,
          }),
          NotificationType.WARNING,
        );
      }
    } catch (error) {
      console.error(error);
      addNotification(
        t("NOTIFICATIONS.DELETE_FAILED", {
          entity: t("PAGES.USERS.HEADER").toLowerCase(),
        }),
      );
    } finally {
      setIsDoingUserAction(false);
    }
  };

  const setMultipleUsersVisibility = async (shouldBeVisible: boolean) => {
    setIsDoingUserAction(true);
    try {
      const selectedUsers = getSelectedItemsInDisplayOrder(
        tableInstanceRef.current,
      );
      const promises = selectedUsers.map(async (user: User) =>
        updateUser(
          user.userId,
          { visibleInVisit: shouldBeVisible },
          undefined,
          companyId,
        ),
      );
      await Promise.all(promises);
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "users"],
      });
      setRowSelection({});
      addNotification(
        t("NOTIFICATIONS.DOCUMENT_UPDATED", {
          entity: `${selectedUsers.length} ${t("PAGES.USERS.HEADER").toLowerCase()}`,
        }),
        NotificationType.SUCCESS,
      );
    } catch (error) {
      console.error(error);
      addNotification(
        t("NOTIFICATIONS.UPDATE_FAILED", {
          entity: t("PAGES.USERS.HEADER").toLowerCase(),
        }),
      );
    } finally {
      setIsDoingUserAction(false);
    }
  };

  const integrateMultipleUsers = async () => {
    if (!company) return;
    const shouldProceed = await checkAndProceed();
    if (!shouldProceed) return;
    setIsDoingUserAction(true);

    const buildingAllowsDigitalBadges = building?.acsDigitalBadgeMode === true;
    const effectiveCompanyDigitalBadgeMode =
      buildingAllowsDigitalBadges && company.acsDigitalBadgeMode === true;

    const sendDigitalAccessEmail =
      effectiveCompanyDigitalBadgeMode &&
      company.acsSendDigitalAccessEmailOnIntegration === true;

    try {
      const selectedUsers = getSelectedItemsInDisplayOrder(
        tableInstanceRef.current,
      );

      const results = await processInBatches(
        selectedUsers,
        20,
        async (user) => {
          const profileImageUrl = await getImageFunction(
            user.userProfileImgUrl,
          );
          return createUserInAccessControlSystem(
            user.email,
            user.firstName,
            user.lastName,
            user.userId,
            company.buildingTenantId,
            company.buildingTenantName,
            effectiveCompanyDigitalBadgeMode,
            sendDigitalAccessEmail,
            user.language,
            companyContact?.name || "",
            building.propertyOwner,
            profileImageUrl,
          );
        },
      );

      const failedIntegrations = results.filter((r) => r.status === "rejected");
      const successfulIntegrations = results.filter(
        (r) => r.status === "fulfilled",
      );

      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "users"],
      });
      setRowSelection({});

      if (successfulIntegrations.length > 0) {
        addNotification(
          t("NOTIFICATIONS.INTEGRATION_SUCCESS", {
            entity: `${successfulIntegrations.length} ${t("PAGES.USERS.HEADER").toLowerCase()}`,
          }),
          NotificationType.SUCCESS,
        );
      }

      if (failedIntegrations.length > 0) {
        addNotification(
          t("NOTIFICATIONS.INTEGRATION_PARTIAL_FAILURE", {
            entity: `${failedIntegrations.length} ${t("PAGES.USERS.HEADER").toLowerCase()}`,
          }),
          NotificationType.WARNING,
        );
      }
    } catch (error) {
      console.error(error);
      addNotification(t("NOTIFICATIONS.INTEGRATION_FAILED"));
    } finally {
      setIsDoingUserAction(false);
    }
  };

  const delayDeletionDate = async () => {
    setIsDoingUserAction(true);
    try {
      const selectedUsers = getSelectedItemsInDisplayOrder(
        tableInstanceRef.current,
      );
      const now = new Date();
      const promises = selectedUsers.map(async (user: User) =>
        updateUser(user.userId, {
          lastActiveDate: now as unknown as Timestamp,
        }),
      );
      await Promise.all(promises);
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "users"],
      });
      setRowSelection({});
      addNotification(
        t("USERS.DELETION_WARNING.DELETION_EXTENDED", {
          newDeletionDate: format(
            endOfQuarter(addMonths(now, 1)),
            "dd.MM.yyyy",
          ),
        }),
        NotificationType.SUCCESS,
      );
    } catch (error) {
      console.error(error);
      addNotification(t("USERS.DELETION_WARNING.DELETION_EXTENSION_FAILED"));
    } finally {
      setIsDoingUserAction(false);
    }
  };

  const handleSubmit = async (data: Data<string>[]) => {
    setImportedData(data);
    setShowSendEmailModal(true);
  };

  const addMultipleUsers = async (data: Data<string>[]) => {
    const usersToAdd = data.map((user) => ({
      firstName: user.firstName as string,
      lastName: user.lastName as string,
      email: user.email as string,
      phone: (user.phone as string) || "",
      buildingTenantId: companyId,
      adminInCompanies: [],
      entranceIds: company?.entranceIds || [],
      visibleInVisit:
        user.visibleInVisit === "true" || user.visibleInVisit === true,
      sendEmail,
      customEmployeeId: (user.customEmployeeId as string) || "",
      jobTitle: (user.jobTitle as string) || "",
      language: (user.language as string) || "",
    }));
    await submitUsers(usersToAdd);
  };

  const exportCompanyUsers = async (
    companyId: string,
    companyName?: string,
    excel?: boolean,
  ) => {
    setExportingUsers(true);
    try {
      await getExportedCompanyUsers(companyId, companyName, { excel });
    } finally {
      setExportingUsers(false);
    }
  };

  const delayDeletionMenuOption = {
    label: t("USERS.DELETION_WARNING.DELAY_DELETION_BUTTON"),
    icon: "auto_delete",
    onSelect: () => delayDeletionDate(),
  };
  const userActionsMenuOptions = [
    {
      label: t("USERS.DELETE_USERS"),
      icon: "delete",
      onSelect: () => setShowDeletionModal(true),
      style: { color: "var(--wlcm-color-danger-red)" },
    },
    ...(showInactiveUsersOnly
      ? [...(showDeletionWarningBanner ? [delayDeletionMenuOption] : [])]
      : [
          ...(showDeletionWarningBanner ? [delayDeletionMenuOption] : []),
          {
            label: t("USERS.MAKE_USERS_VISIBLE"),
            icon: "visibility",
            onSelect: () => setMultipleUsersVisibility(true),
          },
          {
            label: t("USERS.MAKE_USERS_HIDDEN"),
            icon: "visibility_off",
            onSelect: () => setMultipleUsersVisibility(false),
          },
          ...(renderIntegrateUserButton
            ? [
                ...(renderOrderCardButton
                  ? [
                      {
                        label: t("ACCESS_CARD.ORDER_PHYSICAL"),
                        icon: "badge",
                        onSelect: () => setShowOrderModal(true),
                      },
                    ]
                  : []),
                ...(canManageCompanyAccess
                  ? [
                      {
                        label: t("USERS.INTEGRATE_USERS"),
                        icon: "settings_account_box",
                        onSelect: () => integrateMultipleUsers(),
                        tooltip: isAcsDown
                          ? t("ACS_CONNECTIVITY.BUTTON_TOOLTIP")
                          : undefined,
                        disabled: isAcsDown,
                      },
                    ]
                  : []),
              ]
            : []),
          ...(renderFoodButton
            ? [
                {
                  label: t("USERS.ASSIGN_FOOD_DEAL"),
                  icon: "burger",
                  onSelect: () => setShowFoodDealModal(true),
                },
              ]
            : []),
          ...(renderPrintButton
            ? [
                {
                  label: t("BADGE_PRINTING.PRINT.PRINT_MULTIPLE"),
                  icon: "print",
                  onSelect: () => setPrintCardsOpen(true),
                },
              ]
            : []),
          ...(subdivisions?.length
            ? [
                {
                  label: t("USERS.ASSIGN_SUBDIVISION"),
                  icon: "flowchart",
                  onSelect: () => setAssignSubdivisionOpen(true),
                },
              ]
            : []),
        ]),
  ];

  if (!allowCompanyAdminAccess) {
    return null;
  }
  return (
    <>
      {company?.accessControlSystemIntegration && <AcsConnectivityBanner />}
      <div className="pageHeader">
        <h1>{t("PAGES.USERS.HEADER")}</h1>
        <p>
          <Trans i18nKey="PAGES.USERS.SUBHEADER">
            Text
            <a
              href={WELCOME_USER_ADMINISTRATION_URL(i18n.language)}
              target="_blank"
            ></a>
            Text
            <a href={xlsxTemplateFileUrl} download></a>
            Text
            <a href={csvTemplateFileUrl} download></a>
          </Trans>
        </p>
      </div>
      {showDeletionWarningBanner && (
        <div
          className={`banner ${showInactiveUsersOnly ? "warningBanner" : ""}`}
          style={{ marginBottom: "var(--wlcm-spacing-sm)" }}
        >
          <Icon name="info" />
          <p>
            <Trans
              i18nKey={`USERS.DELETION_WARNING.BANNER${showInactiveUsersOnly ? "_INACTIVE_ONLY" : ""}`}
              values={{
                deletionDate: format(endOfQuarter(new Date()), "dd.MM.yyyy"),
              }}
              components={{
                bold: <strong></strong>,
                button: <Link to="." search={{ inactive: true }}></Link>,
              }}
            />
          </p>
        </div>
      )}
      <div className={styles.toolContainer}>
        <div>
          <InputField
            label={t("USERS.SEARCH_PLACEHOLDER")}
            placeholder={t("USERS.SEARCH_PLACEHOLDER") + "..."}
            icon="search"
            value={searchTerm}
            compact
            hideLabel
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <AnimatePresence>
            {showInactiveUsersOnly && (
              <motion.div
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
              >
                <Button
                  dark
                  onClick={() => navigate({ to: ".", search: {} })}
                  icon="undo"
                >
                  {t("USERS.DELETION_WARNING.SHOW_ALL_BUTTON")}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div>
          <AnimatePresence>
            {Object.keys(rowSelection).length > 0 && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
              >
                <DropdownMenu
                  id="users-actions"
                  label={`${t("COMPANY_SETTINGS.ADMINS.ACTIONS")} (${selectedUsersCount} ${t("USER", { count: selectedUsersCount }).toLowerCase()})`}
                  menuOptions={userActionsMenuOptions}
                  disabled={isDoingUserAction || isRolesCheckWaiting}
                  isLoading={isDoingUserAction || isRolesCheckWaiting}
                />
              </motion.div>
            )}
          </AnimatePresence>
          <Button icon="upload" onClick={() => setBulkUploadOpen(true)}>
            {t("USERS.BULK_UPLOAD.BULK_UPLOAD")}
          </Button>
          <Button icon="add" onClick={() => setAddUserOpen(true)}>
            {t("USERS.ADD_USER")}
          </Button>
        </div>
      </div>
      <Suspense
        fallback={
          <div
            style={{
              height: 250,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LoadingSpinner />
          </div>
        }
      >
        <UsersTable
          isLoading={isLoading || isLoadingMetaData}
          filteredUsers={filteredUsers}
          companyId={companyId}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          onTableReady={handleTableReady}
        />
      </Suspense>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: "var(--wlcm-spacing-sm)",
        }}
      >
        <DropdownMenu
          id="company-users-export"
          activator={
            <Button
              isLoading={exportingUsers}
              disabled={exportingUsers}
              dark
              secondary
              icon="file_export"
            >
              {t("COMPANIES_TABLE.EXPORT_ALL_USERS")}
            </Button>
          }
          menuOptions={[
            {
              icon: "csv",
              label: "CSV",
              onSelect: () =>
                exportCompanyUsers(
                  companyId,
                  company?.buildingTenantName,
                  false,
                ),
            },
            {
              icon: "microsoft_excel",
              label: "Microsoft Excel",
              onSelect: () =>
                exportCompanyUsers(
                  companyId,
                  company?.buildingTenantName,
                  true,
                ),
            },
          ]}
        />
      </div>
      <AddUserModal open={addUserOpen} onClose={() => setAddUserOpen(false)} />
      {bulkUploadOpen && company && (
        <Suspense>
          <SpreadsheetImporter
            open={bulkUploadOpen}
            company={company}
            onClose={() => setBulkUploadOpen(false)}
            onSubmit={(data) => handleSubmit(data.validData)}
          />
        </Suspense>
      )}
      <PopupCard
        onClose={() => setShowSendEmailModal(false)}
        open={showSendEmailModal}
      >
        <div className={styles.bulkUploadConfirmationModal}>
          <h3>{t("USERS.CONFIRM_USER_UPLOAD")}</h3>
          <p>
            {t("USERS.CONFIRM_USER_UPLOAD_DESCRIPTION", {
              count: importedData.length,
            })}
          </p>
          <div className={styles.sendEmailToggleContainer}>
            <Toggle
              label={t("USERS.SEND_EMAIL")}
              checked={sendEmail}
              onChange={setSendEmail}
              dark
            />
            <p>
              {sendEmail
                ? t("USERS.SEND_EMAIL_DESCRIPTION")
                : t("USERS.SEND_NO_EMAIL_DESCRIPTION")}
            </p>
          </div>

          {company?.accessControlSystemIntegration &&
            company?.acsAutomaticProvisioningMode && (
              <div
                className={`highlightedInfo ${styles.fullWidth}`}
                style={{ marginTop: "var(--wlcm-spacing-md)" }}
              >
                <Icon name="info" />
                {t("ADD_USER.AUTOPROVISIONING_DESCRIPTION")}{" "}
                {building?.acsDigitalBadgeMode &&
                  company.acsSendDigitalAccessEmailOnIntegration &&
                  company.acsDigitalBadgeMode &&
                  t("ADD_USER.DIGITAL_EMAIL_DESCRIPTION")}
              </div>
            )}
          <Button
            onClick={() => {
              addMultipleUsers(importedData);
            }}
            isLoading={isSubmitting}
          >
            {t("USERS.CONFIRM")}
          </Button>
        </div>
      </PopupCard>

      {company && (
        <PopupCard
          onClose={() => setShowFoodDealModal(false)}
          open={showFoodDealModal}
        >
          <FoodDealModal
            users={getSelectedItemsInDisplayOrder(tableInstanceRef.current)}
            company={company}
            onClose={() => setShowFoodDealModal(false)}
          />
        </PopupCard>
      )}
      <PopupCard
        onClose={() => setPrintCardsOpen(false)}
        wide
        open={printCardsOpen}
      >
        <PrintAccessCard
          users={getSelectedItemsInDisplayOrder(tableInstanceRef.current)}
          companyId={company?.buildingTenantId || ""}
          companyName={company?.buildingTenantName || ""}
          onClose={() => setPrintCardsOpen(false)}
        />
      </PopupCard>
      {showOrderModal && company && (
        <BulkCardOrdersModal
          company={company}
          users={getSelectedItemsInDisplayOrder(tableInstanceRef.current)}
          onClose={() => {
            setShowOrderModal(false);
            setRowSelection({});
          }}
          open={showOrderModal}
        />
      )}
      <PopupCard
        onClose={() => setAssignSubdivisionOpen(false)}
        open={assignSubdivisionOpen}
      >
        <BulkAssignSubdivision
          users={getSelectedItemsInDisplayOrder(tableInstanceRef.current)}
          companyId={companyId}
          onSuccess={() => {
            setAssignSubdivisionOpen(false);
            setRowSelection({});
          }}
        />
      </PopupCard>
    </>
  );
}
